import { ClickAwayListener, IconButton, Stack, Tooltip, styled, tooltipClasses } from '@mui/material';
import { Operations } from '@src/api';
import { theme } from '@src/theme';
import { FC, useState } from 'react';
import { ReactComponent as IconLessOrEquals } from '@icons/Math/less_equals.svg';
import { ReactComponent as IconLess } from '@icons/Math/less.svg';
import { ReactComponent as IconEquals } from '@icons/Math/equals.svg';
import { ReactComponent as IconNotEquals } from '@icons/Math/not_equal.svg';
import { ReactComponent as IconGreated } from '@icons/Math/greater.svg';
import { ReactComponent as IconGreaterOrEqual } from '@icons/Math/greater_or_equal.svg';
import { buildDataTestIdAttribute, hexOpacity, replaceCebabCase } from '@src/utils';

export const OperationButton = styled(IconButton)(() => ({
  minWidth: 0,
  backgroundColor: '#FFF',
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: 0,
  height: '42px',
  width: '42px',
  '& svg path': {
    fill: theme.palette.secondary.main,
  },
  '&:hover': {
    border: `1px solid ${theme.palette.secondary.light}`,
    '& svg path': {
      fill: theme.palette.secondary.light,
    },
  },
  '&:active': {
    border: `2px solid ${theme.palette.secondary.dark}`,
    '& svg path': {
      fill: theme.palette.secondary.dark,
    },
  },
  '&.Mui-disabled': {
    border: `1px solid ${theme.palette.grey['300']}`,
    background: theme.palette.grey['200'],
    '& svg path': {
      fill: theme.palette.grey['300'],
    },
  },
}));

const OperationButtonTooltip = styled(IconButton)(() => ({
  minWidth: 0,
  backgroundColor: '#FFF',
  borderRadius: 0,
  height: '42px',
  width: '42px',
  '&:hover': {
    backgroundColor: hexOpacity(theme.palette.secondary.main, '7%'),
    color: theme.palette.secondary.main,
  },
  '&:active': {
    backgroundColor: hexOpacity(theme.palette.secondary.main, '14%'),
    color: theme.palette.secondary.dark,
  },
}));

export const arrayOperation = [
  { icon: IconLessOrEquals, value: Operations.LessOrEqual },
  { icon: IconLess, value: Operations.Less },
  { icon: IconNotEquals, value: Operations.NotEqual },
  { icon: IconEquals, value: Operations.Equals },
  { icon: IconGreated, value: Operations.Greater },
  { icon: IconGreaterOrEqual, value: Operations.GreaterOrEqual },
];

type OperationTooltionProps = {
  onChangeOperation: (val: Operations) => void;
  opeartionValue: Operations;
  disabled?: boolean;
  dataTestId?: string;
};
export const OperationButtonWithTooltip: FC<OperationTooltionProps> = ({
  onChangeOperation,
  opeartionValue,
  disabled,
  dataTestId,
}) => {
  const [open, setOpen] = useState(false);
  const IconOperaition = arrayOperation.find(({ value }) => value === opeartionValue)?.icon;

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleOperationClick = () => {
    setOpen(true);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div>
        <Tooltip
          slotProps={{
            popper: {
              sx: {
                [`& .${tooltipClasses.arrow}`]: {
                  color: '#fff',
                  width: '28px',
                  height: '14px',
                },
                [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.arrow}`]: {
                  marginBottom: '-14px',
                },
                [`& .${tooltipClasses.tooltip}`]: {
                  backgroundColor: '#fff',
                  padding: 0,
                  boxShadow: '-2px -2px 6px 1px #0000001A, 2px 2px 6px 1px #0000001A',
                },
                [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]: {
                  marginBottom: '18px',
                },
              },
            },
          }}
          PopperProps={{
            disablePortal: true,
            sx: { padding: 0 },
          }}
          sx={{ padding: 0 }}
          onClose={handleTooltipClose}
          open={open}
          arrow
          placement="top"
          disableFocusListener
          disableHoverListener
          disableTouchListener
          children={
            <OperationButton
              sx={
                open
                  ? {
                      border: `2px solid ${theme.palette.secondary.dark}`,
                      '& svg path': {
                        fill: theme.palette.secondary.dark,
                      },
                    }
                  : null
              }
              onClick={handleOperationClick}
              disabled={disabled}
              {...buildDataTestIdAttribute(dataTestId, 'button-operation')}
            >
              {IconOperaition && <IconOperaition />}
            </OperationButton>
          }
          title={
            <Stack direction="row">
              {arrayOperation.map(({ icon: Icon, value }) => (
                <OperationButtonTooltip
                  sx={{
                    '&:hover': {
                      svg: {
                        path: {
                          fill: 'currentColor',
                        },
                      },
                    },
                  }}
                  key={value}
                  onClick={() => {
                    onChangeOperation(value);
                    handleTooltipClose();
                  }}
                  {...buildDataTestIdAttribute(dataTestId, `button-${replaceCebabCase(value)}`)}
                >
                  <Icon />
                </OperationButtonTooltip>
              ))}
            </Stack>
          }
        />
      </div>
    </ClickAwayListener>
  );
};
